import React, { useMemo } from 'react';
import Toggle from 'v3/components/Toggle';
import Select from 'v3/components/Select';
import Button from 'v3/components/Button';
import Spinner from 'react-bootstrap/Spinner';
import Text from 'v3/components/Text';
import moment from 'moment';

const PERIOD_OPTIONS = [
  { label: '3 dias',  value: '3d'  },
  { label: '10 dias', value: '10d' },
  { label: '30 dias', value: '30d' },
];

const GLOBAL_STATUS = [
  { label: 'Em andamento',   value: 'IN_ANALYSIS' },
  { label: 'Não iniciadas',  value: 'NOT_STARTED' },
  { label: 'Críticas > 3h',  value: 'CRITICAL' },
  { label: 'Em revalidação', value: 'PENDING_ADJUSTMENT' },
  { label: 'Aprovadas',      value: 'APPROVED' },
];

export const SECTOR_STATUS = [
  { label: 'Aprovado',      value: 'APPROVED' },
  { label: 'Reprovado',     value: 'REPROVED' },
  { label: 'Em andamento',  value: 'IN_PROGRESS' },
  { label: 'Não iniciado',  value: 'NOT_STARTED' },
];

const SECTORS = [
  { label: 'Operação',   value: 'OPERATION' },
  { label: 'Cadastro',   value: 'REGISTER' },
  { label: 'GRO',        value: 'RISK_MANAGER' },
  { label: 'Negociação', value: 'NEGOTIATOR_CUSTOMER' },
  { label: 'Emissão',    value: 'EMISSION' },
];

const FieldWrap = ({ children, helper, error }) => (
  <div style={{ position: 'relative' }}>
    {children}
    {helper ? (
      <small
        style={{
          position: 'absolute',
          left: 0,
          top: '100%',
          marginTop: 4,
          lineHeight: '16px',
          fontSize: 12,
          color: error ? '#e53935' : 'inherit',
          pointerEvents: 'none',
        }}
      >
        {helper}
      </small>
    ) : null}
  </div>
);

const localSearch =
  (options = []) =>
  async (q = '') => {
    const s = String(q).toLowerCase();
    return options.filter(o => (o.label || '').toLowerCase().includes(s));
  };

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const optsFromValues = (values, options) => options.filter(o => values.includes(o.value));
const valuesFromOpts = (opt) =>
  Array.isArray(opt) ? opt.map(o => o.value) : opt ? [opt.value] : [];

export default function TopFilter({
  filter,
  onFilter,
  onSubmit,
  resultsTotal,
  loadingResults,
}) {
  const sectorValues = useMemo(() => toArray(filter.sectorSlug), [filter.sectorSlug]);
  const sectorStatusValues = useMemo(() => toArray(filter.sectorStatus), [filter.sectorStatus]);
  const travelIndicatorValues = useMemo(() => toArray(filter.travelIndicatorStatus), [filter.travelIndicatorStatus]);
  const needsSectorStatus   = sectorValues.length > 0;
  const sectorStatusRequired = needsSectorStatus && sectorStatusValues.length === 0;

  const flexGrowInput = { flex: '1 1 0px', minWidth: 260 };
  const fixedToggle   = { flex: '0 0 300px', minWidth: 300 };
  const fixedButton   = { flex: '0 0 160px' };

  function onChangePeriod(value) {
    onFilter({
      ...filter,
      startDate: moment().startOf('day').subtract(Number(value.replace(/\D/g, '')), 'days').toISOString(),
      endDate: null,
    })
  }

  const period = useMemo(() => {
    let endDate = filter.endDate ? moment(filter.endDate) : moment().endOf('day');
    let startDate = moment(filter.startDate);

    if (endDate.diff(startDate, 'days') === 3) {
      return '3d';
    } else if (endDate.diff(startDate, 'days') === 10) {
      return '10d';
    } else if (endDate.diff(startDate, 'days') === 30) {
      return '30d';
    }

    return '';
  }, [filter.startDate, filter.endDate]);

  return (
    <div
      className="filter top-filter"
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        gap: 12,
        paddingBottom: 22,
        width: '100%',
        overflow: 'visible',
      }}
    >
      <div style={fixedToggle}>
        <FieldWrap>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
            <Toggle
              label="Criado em até"
              labelColor="#fff"
              value={period}
              defaultValue={period}
              options={PERIOD_OPTIONS}
              onSelected={onChangePeriod}
            />
          </div>
        </FieldWrap>
      </div>

      <div style={flexGrowInput}>
        <FieldWrap>
          <Select.Async
            label="Setor"
            labelColor="#fff"
            multiple
            options={SECTORS}
            defaultOptions={SECTORS}
            onSearch={localSearch(SECTORS)}
            value={optsFromValues(sectorValues, SECTORS)}
            onChange={(opt) => {
              const values = valuesFromOpts(opt);
              onFilter({
                ...filter,
                sectorSlug: values,
              });

              if (values.length === 0) {
                onFilter({
                  ...filter,
                  sectorSlug: [],
                  sectorStatus: [],
                });
              }
            }}
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value}
            placeholder="Selecione..."
            useMenuPortal
            selectValuesContainerMaxHeight="44px"
            selectValuesContainerOverflowY="auto"
          />
        </FieldWrap>
      </div>

      {sectorValues.length > 0 && (
        <div style={flexGrowInput}>
          <FieldWrap
            helper={sectorStatusRequired ? 'Selecione o status do setor.' : ''}
            error={sectorStatusRequired}
          >
            <Select.Async
              label="Status do setor"
              labelColor="#fff"
              multiple
              options={SECTOR_STATUS}
              defaultOptions={SECTOR_STATUS}
              onSearch={localSearch(SECTOR_STATUS)}
              value={optsFromValues(sectorStatusValues, SECTOR_STATUS)}
              onChange={(opt) =>
                onFilter({
                  ...filter,
                  sectorStatus: valuesFromOpts(opt),
                })
              }
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value}
            placeholder="Selecione..."
            useMenuPortal
            selectValuesContainerMaxHeight="44px"
            selectValuesContainerOverflowY="auto"
          />
        </FieldWrap>
      </div>
      )}

      <div style={flexGrowInput}>
        <FieldWrap>
          <Select.Async
            label="Status validação"
            labelColor="#fff"
            multiple
            options={GLOBAL_STATUS}
            defaultOptions={GLOBAL_STATUS}
            onSearch={localSearch(GLOBAL_STATUS)}
            value={optsFromValues(travelIndicatorValues, GLOBAL_STATUS)}
            onChange={(opt) =>
              onFilter({
                ...filter,
                travelIndicatorStatus: valuesFromOpts(opt),
              })
            }
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value}
            placeholder="Selecione..."
            useMenuPortal
            selectValuesContainerMaxHeight="44px"
            selectValuesContainerOverflowY="auto"
          />
        </FieldWrap>
      </div>

      <div style={{ position: 'relative', ...fixedButton, alignSelf: 'flex-end' }}>
        <Button
          variant="success"
          onClick={() => onSubmit(filter)}
          className="w-100 d-flex align-items-center justify-content-center"
          disabled={sectorStatusRequired}
          title={sectorStatusRequired ? 'Selecione o status do setor' : undefined}
          style={{
            height: 44,
            lineHeight: 'normal',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
          }}
        >
          <Text type="regular" weight={500}>Filtrar</Text>
        </Button>
        {Number(resultsTotal) >= 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 6,
              width: '100%',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
            aria-live="polite"
          >
            {loadingResults ? (
              <div className="d-inline-flex align-items-center justify-content-center" style={{ gap: 8 }}>
                <Spinner animation="border" size="sm" className="text-white" />
                <Text type="regular" color="white" weight={400}>Carregando</Text>
              </div>
            ) : (
              <Text type="regular" color="white" weight={400}>
                {resultsTotal} {resultsTotal === 1 ? 'viagem' : 'viagens'}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
